<template v-if="this.item">
  <div :class="['basket-item', basketItemState]">
    <div class="area-separator" />
    <div class="area-image">
      <img v-if="productImage" :src="productImage" :alt="productName" />
      <aspect-ratio v-else ratio="4x3" class="ratio-4x3 is-fallback-image">
        <b-icon icon="images" />
      </aspect-ratio>
    </div>

    <div class="area-description">
      <p class="mb-0"><span class="item-id">{{ productId }}</span></p>
      <p><span class="item-name" v-if="productModel">{{ productModel }}</span> {{ productName }}</p>
      <p v-if="productBundleCount > 0">
        {{ $t('components.order.basket.basketitem.bundlecount_text', { bundleCount: productBundleCount }) }}
      </p>
      <p class="mb-0"
         v-if="item.configuration && item.configuration.additionalConfigurations && item.configuration.additionalConfigurations.dateStart && item.configuration.additionalConfigurations.dateEnd">
        <span>{{ $t('components.order.basket.basketitem.deliverytimeslot') }} {{ item.configuration.additionalConfigurations.dateStart | formatDate }} - {{
          item.configuration.additionalConfigurations.dateEnd | formatDate }}</span>
      </p>
    </div>

    <div class="area-quantity">
      <span v-if="!editQuantity">{{ itemQuantity }}</span>
      <div class="d-flex justify-content-end align-items-center" v-if="editQuantity">
        <b-input :type="'number'" v-model="itemQuantityUpdateValue" :autocomplete="'false'" class="w-25" :disabled="isUpdatingQuantity" />
        <loader v-if="isUpdatingQuantity" type="dots" :inline="true" :scale="0.8" class="ml-2" />
        <b-button :title="$t('components.order.basket.basketitem.edit_button_cancel')" v-if="editQuantity && !isUpdatingQuantity" @click.prevent="cancelEditQuantity" variant="link"><b-icon
                  variant="danger" icon="close" aria-hidden="true"></b-icon></b-button>
        <b-button :title="$t('components.order.basket.basketitem.edit_button_save')" v-if="editQuantity && !isUpdatingQuantity" @click.prevent="updateQuantity" variant="link"><b-icon variant="success"
                  icon="check" aria-hidden="true"></b-icon></b-button>
      </div>
      <b-button :title="$t('components.order.basket.basketitem.edit_button_edit')" v-if="isEditable && !editQuantity" @click.prevent="editQuantity = true" variant="link"><b-icon icon="b-pencil-fill"
                aria-hidden="true"></b-icon></b-button>
    </div>

    <div class="area-warehouse">
      <b-badge v-if="productWarehouse === 'ebi-pharm'" variant="primary">{{ $t('components.order.basket.basketitem.warehouse_gus') }}</b-badge>
      <b-badge v-else-if="productWarehouse === 'ruefenacht'" variant="warning">{{ $t('components.order.basket.basketitem.warehouse_rufenacht') }}</b-badge>
      <b-badge v-else variant="danger">{{ $t('components.order.basket.basketitem.warehouse_missing') }}</b-badge>
    </div>

    <div class="area-actions">
      <b-button :title="$t('components.order.basket.basketitem.confirm_deleted_button_ok')" v-if="isEditable" @click="openModal('deleted')" variant="warning"><b-icon icon="trash"></b-icon></b-button>
      <b-button :title="$t('components.order.basket.basketitem.confirm_active_button_ok')" v-if="isEditableWithoutState && basketItemState === 'deleted'" @click="openModal('active')"
                variant="success"><b-icon icon="reset"></b-icon></b-button>
    </div>

    <div class="area-separator" />
    <b-modal :id="confirmationModal.id" v-model="confirmationModal.show" size="md" centered scrollable no-fade header-bg-variant="warning" header-text-variant="black" title-tag="h4"
             title-class="align-self-center" hide-footer>
      <template #modal-header-close><b-icon icon="close" scale="0.6" /></template>
      <template #modal-title>{{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_item_title`) }}</template>

      <template v-if="confirmationModal.show">
        <div>
          {{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_item`) }}
        </div>

        <div class="d-flex justify-content-end mt-3">
          <div class="btn-list">
            <b-button variant="light" size="sm" @click="closeModal">
              <slot name="modalAbortButton" :type="confirmationModal.type" />{{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_button_cancel`) }}
            </b-button>
            <b-button variant="danger" size="sm" @click.prevent="changeStateBasketItem(confirmationModal.type)"><b-icon icon="trash" /> {{
              $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_button_ok`) }}</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>

import { TOASTER_OPTIONS } from '@/assets/js/config/client'
import {
  getProductBundleCount,
  getProductId,
  getProductImage,
  getProductModel,
  getProductName,
  getProductWarehouse
} from '@/assets/js/helper/product'
import AspectRatio from '@/components/AspectRatio'
import Loader from '@/components/Loader'

export default {
  name: 'BasketItem',
  components: {
    AspectRatio,
    Loader
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    order: {
      type: Object
    }
  },
  data() {
    return {
      itemQuantityUpdateValue: this.item.quantity,
      confirmationModal: {
        id: `${this.$options.name}_ConfirmationModal`,
        show: false,
        type: 'delete'
      },
      editQuantity: false,
      isUpdatingQuantity: false,
      editableOrderStates: ['Open', 'Executed', 'Ordered', 'Processing']
    }
  },
  computed: {
    itemQuantity() {
      return this.item.quantity
    },
    basketItemState() {
      return this.item.state?.toLowerCase()
    },
    productName() {
      return getProductName(this.item.product, this.order)
    },
    productId() {
      return getProductId(this.item.product)
    },
    productImage() {
      return getProductImage(this.item.product)
    },
    productModel() {
      return getProductModel(this.item.product)
    },
    productWarehouse() {
      return getProductWarehouse(this.item.product)
    },
    productBundleCount() {
      return getProductBundleCount(this.item.product)
    },
    isEditable() {
      return this.isEditableWithoutState && this.basketItemState === 'active'
    },
    transmissions() {
      // FullOrderModel might have transmissions at root or in order.Order.transmissions
      // Check multiple locations and normalize like OrderActions component does
      // Also check for Transmissions (capital T) as EbiOnlineFullOrderModel has Transmissions property
      const rawTransmissions = this.order?.transmissions ||
        this.order?.Transmissions ||
        this.order?.Order?.transmissions ||
        this.order?.Order?.Transmissions ||
        {}

      // Normalize keys to PascalCase to handle both camelCase and PascalCase from API
      const normalized = {}
      for (const key in rawTransmissions) {
        if (Object.prototype.hasOwnProperty.call(rawTransmissions, key)) {
          // Convert first character to uppercase if it's lowercase (camelCase -> PascalCase)
          const normalizedKey = key.length > 0 && key[0] === key[0].toLowerCase()
            ? key[0].toUpperCase() + key.slice(1)
            : key
          normalized[normalizedKey] = rawTransmissions[key]
        }
      }

      return normalized
    },
    isEditableWithoutState() {
      // Check if order is in an editable state
      const orderState = this.order?.orderState
      let editable = this.editableOrderStates.includes(orderState)

      // Check if transmissions object exists and has any keys
      const hasTransmissions = this.order?.transmissions || this.order?.Order?.transmissions
      const transmissionsKeys = Object.keys(this.transmissions)
      const hasAnyTransmissions = hasTransmissions && transmissionsKeys.length > 0

      // If no transmissions exist or transmissions is empty, allow editing based on order state only
      // This handles cases where FullOrderModel doesn't have transmissions yet or transmissions is empty {}
      if (!hasAnyTransmissions) {
        return editable
      }

      // Check transmission status based on warehouse
      const warehouse = this.productWarehouse
      if (warehouse) {
        if (warehouse === 'ebi-pharm') {
          const transmission = this.transmissions.TransmitToExternalOrderManagement
          const isNotTransmitted = transmission && (transmission.state === 'NotTransmitted' || transmission.State === 'NotTransmitted')
          editable = editable && !!isNotTransmitted
        } else if (warehouse === 'ruefenacht') {
          const transmission = this.transmissions.TransmitPerEmail
          const isNotTransmitted = transmission && (transmission.state === 'NotTransmitted' || transmission.State === 'NotTransmitted')
          editable = editable && !!isNotTransmitted
        } else {
          editable = false
        }
      } else {
        // No warehouse - check TransmitNotPossible transmission
        const transmission = this.transmissions.TransmitNotPossible
        const isNotTransmitted = transmission && (transmission.state === 'NotTransmitted' || transmission.State === 'NotTransmitted')
        // If transmission doesn't exist, allow editing (same as empty transmissions case)
        editable = editable && (transmission ? !!isNotTransmitted : true)
      }

      return !!editable // Ensure we always return a boolean
    }
  },
  methods: {
    changeStateBasketItem(itemState) {
      this.$store.dispatch('orders/updateOrderItem', {
        orderId: this.order.orderId || this.order.id,
        basketItemId: this.item.basketItemId,
        data: {
          itemState: itemState.charAt(0).toUpperCase() + itemState.slice(1)
        }
      }).finally(() => {
        this.confirmationModal.show = false
      })
    },
    updateQuantity() {
      this.isUpdatingQuantity = true
      this.$store.dispatch('orders/updateOrderItem', {
        orderId: this.order.orderId || this.order.id,
        basketItemId: this.item.basketItemId,
        data: {
          quantity: this.itemQuantityUpdateValue
        }
      })
        .then(() => {
          // Show success toast
          if (this.$app && this.$app.$bvToast) {
            this.$app.$bvToast.toast(
              this.$t('components.order.basket.basketitem.quantity_updated'),
              Object.assign({}, TOASTER_OPTIONS.api.options.send, {
                title: this.$t('components.order.basket.basketitem.quantity_updated_title')
              })
            )
          }
          // Close editor after successful update
          this.editQuantity = false
        })
        .catch(error => {
          console.error('Error updating quantity:', error)
          // Keep editor open on error so user can retry
        })
        .finally(() => {
          this.isUpdatingQuantity = false
        })
    },
    cancelEditQuantity() {
      // Reset to original value and close editor
      this.itemQuantityUpdateValue = this.item.quantity
      this.editQuantity = false
      this.isUpdatingQuantity = false
    },
    openModal(type) {
      this.confirmationModal.type = type
      this.confirmationModal.show = true
    },
    closeModal() {
      this.confirmationModal.show = false
    }
  }
}
</script>

<style lang="scss">
$basketitem-separator-gap-y: $spacer * 0.4 !default;
$basketitem-separator-border: $border-width solid $border-color !default;

$basketitem-image-width: 100px !default;
$basketitem-img-fallback-size: 100% !default;
$basketitem-img-fallback-color: $light !default;

$basketitem-description-name-font-weight: $font-weight-bold !default;
$basketitem-description-name-text-transform: uppercase !default;

$basketitem-configuration-gap-y: 0 !default;
$basketitem-configuration-gap-x: $spacer * 1.2 !default;

$basketitem-totalprice-font-weight: $font-weight-bold !default;

.order-basket {
  .basket-items {
    .basket-item {
      display: contents;

      &.deleted {
        > * {
          text-decoration: line-through;
        }
      }

      .area-separator {
        margin-top: $basketitem-separator-gap-y;
        margin-bottom: $basketitem-separator-gap-y;
        border-top: $basketitem-separator-border;
      }

      .area-image {

        img,
        .is-fallback-image {
          display: block;
          margin: 0;
          width: $basketitem-image-width;
          max-width: none;

          .bi {
            display: block;
            margin: auto;
            width: $basketitem-img-fallback-size;
            height: $basketitem-img-fallback-size;
            color: $basketitem-img-fallback-color;
          }
        }
      }

      .area-description {
        .item-name {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }
      }

      .area-description {
        .item-id {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }
      }

      .area-description {
        .item-bundlecount {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }
      }

      .area-warehouse {
        padding: 4px 0;
      }

      .area-configuration {
        .item-configuration {
          display: flex;
          flex-wrap: wrap;
          margin: ($basketitem-configuration-gap-y * -0.5) ($basketitem-configuration-gap-x * -0.5);

          span {
            display: block;
            padding: ($basketitem-configuration-gap-y * 0.5) ($basketitem-configuration-gap-x * 0.5);
          }
        }
      }

      .area-price,
      .area-totalprice {
        white-space: nowrap;
      }

      .area-totalprice {
        font-weight: $basketitem-totalprice-font-weight;
      }

      + .basket-item {
        .area-separator {
          &:first-child {
            display: none;
          }
        }
      }
    }
  }
}
</style>
